import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";

const AdminHomepage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Decode user from token
  const token = localStorage.getItem("token");
  let user = { username: "Admin", unit: "Pusat" };
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      user = payload;
    } catch (e) {
      console.error("Failed to decode token", e);
    }
  }

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch tasks
        const tasksRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/task`,
          { headers }
        );

        // Fetch pending users
        let usersResData = [];
        try {
          const usersRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/approve-user`,
            { headers }
          );
          usersResData = usersRes.data.data || [];
        } catch (err) {
          console.warn("Failed to fetch pending users, skipping...", err);
        }

        setTasks(tasksRes.data.data || []);
        setPendingUsers(usersResData);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Gagal memuat data dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format current date in Indonesian
  const getFormattedDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('id-ID', options);
  };

  // Metrics calculations
  const totalTasks = tasks.length;
  const draftTasks = tasks.filter(t => t.status === 0).length;
  const assignedTasks = tasks.filter(t => t.status === 1).length;

  const assignedPercentage = totalTasks > 0 ? Math.round((assignedTasks / totalTasks) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-semibold text-sm">Memuat Dashboard Overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title={"Dashboard Overview"} />

      {/* Main content wrapper shifted to right for sidebar */}
      <div className="px-6 pt-24 pb-12 max-w-7xl mx-auto">
        
        {/* Welcome Section */}
        <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 md:p-8 shadow-md border border-slate-800">
          <div className="absolute right-0 top-0 -mt-4 -mr-4 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl"></div>
          <div className="absolute left-1/3 bottom-0 -mb-8 w-60 h-60 bg-blue-500/10 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Sistem Online
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                Selamat Datang, {user.username}!
              </h2>
              <p className="text-sm text-slate-400 mt-1 font-medium max-w-xl">
                Unit Kerja: {user.unit || "Kantor Pusat"}. Kelola penugasan ganti meter lapangan, verifikasi akun petugas, dan sinkronisasi database pelanggan secara terpusat.
              </p>
            </div>
            <div className="bg-slate-800/50 backdrop-blur-md px-4 py-3 rounded-xl border border-slate-700/50 flex flex-col items-start md:items-end shrink-0">
              <span className="text-[10px] text-indigo-400 font-extrabold uppercase tracking-wider">Tanggal Hari Ini</span>
              <span className="text-sm font-bold text-white mt-0.5">{getFormattedDate()}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-sm font-medium flex items-center gap-2">
            <svg className="w-5 h-5 shrink-0 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Card 1: Total Tasks */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">Total Penugasan</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{totalTasks}</h3>
              </div>
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-xs font-semibold text-indigo-600">
              <span>Agenda KWH Meter terdaftar</span>
            </div>
          </div>

          {/* Card 2: Assigned Tasks */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">Tugas Ditugaskan</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{assignedTasks}</h3>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex justify-between items-center text-xs font-bold text-slate-500 mb-1">
                <span>Rasio Ditugaskan</span>
                <span className="text-emerald-600">{assignedPercentage}%</span>
              </div>
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-emerald-500 h-full rounded-full transition-all duration-500" 
                  style={{ width: `${assignedPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Card 3: Draft Tasks */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">Menunggu Penugasan</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{draftTasks}</h3>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-amber-600">Draft baru dibuat</span>
              {draftTasks > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 animate-pulse">
                  Butuh Plotting
                </span>
              )}
            </div>
          </div>

          {/* Card 4: Pending Users */}
          <div 
            onClick={() => navigate("/approve-user")}
            className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between cursor-pointer group"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wider group-hover:text-indigo-600 transition-colors">Persetujuan Petugas</p>
                <h3 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight">{pendingUsers.length}</h3>
              </div>
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600 group-hover:bg-rose-100 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-500">
              {pendingUsers.length > 0 ? (
                <>
                  <span className="text-rose-600">Menunggu review admin</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 font-extrabold">VERIFIKASI</span>
                </>
              ) : (
                <span className="text-slate-400">Semua petugas terverifikasi</span>
              )}
            </div>
          </div>
        </div>

        {/* Content Section: 2 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Column 1 & 2: Recent Task List */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Penugasan Terbaru</h3>
                  <p className="text-xs font-medium text-slate-500 mt-0.5">Daftar agenda pengerjaan ganti meter terbaru</p>
                </div>
                <Link 
                  to="/histori-task" 
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition"
                >
                  Lihat Semua →
                </Link>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider">No Agenda</th>
                      <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider">Unit UP</th>
                      <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider">Batas Waktu</th>
                      <th className="pb-3 text-xs font-extrabold text-slate-400 uppercase tracking-wider text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.slice(0, 5).map((task) => (
                      <tr key={task.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0">
                        <td className="py-3.5 text-sm font-bold text-slate-900">
                          <Link to={`/task-detail/${task.id}`} className="text-indigo-600 hover:text-indigo-800 transition hover:underline">
                            {task.task_no}
                          </Link>
                        </td>
                        <td className="py-3.5 text-sm font-semibold text-slate-500">{task.unit}</td>
                        <td className="py-3.5 text-sm font-medium text-slate-500">{task.dl}</td>
                        <td className="py-3.5 text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            task.status === 1
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : task.status === 2
                                ? "bg-rose-50 text-rose-700 border border-rose-200"
                                : task.status === 3
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-slate-50 text-slate-700 border border-slate-200"
                          }`}>
                            {task.status === 1
                              ? "Ditugaskan"
                              : task.status === 2
                                ? "Dibatalkan"
                                : task.status === 3
                                  ? "Overdue"
                                  : "Draft"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {tasks.length === 0 && (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-sm font-medium text-slate-400">
                          Belum ada data penugasan ganti meter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Column 3: Quick Action Module Cards */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Akses Cepat Modul</h3>
              
              <div className="space-y-4">
                {/* Action 1: Ganti Meter */}
                <Link
                  to="/task-home"
                  className="flex items-center gap-3.5 p-3 rounded-xl border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">
                      Modul Ganti Meter
                    </h4>
                    <p className="text-xs text-slate-500 font-medium">Buat tugas & plot petugas lapangan</p>
                  </div>
                </Link>

                {/* Action 2: Petugas */}
                <Link
                  to="/access"
                  className="flex items-center gap-3.5 p-3 rounded-xl border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 group-hover:text-emerald-600 transition-colors">
                      Manajemen Petugas
                    </h4>
                    <p className="text-xs text-slate-500 font-medium">Daftar & berikan persetujuan akses</p>
                  </div>
                </Link>

                {/* Action 3: Database */}
                <Link
                  to="/database"
                  className="flex items-center gap-3.5 p-3 rounded-xl border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4M4 7c0-2.21 3.58-4 8-4s8 1.79 8 4m0 5c0 2.21-3.58 4-8 4s-8-1.79-8-4" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800 group-hover:text-purple-600 transition-colors">
                      Sinkronisasi Database
                    </h4>
                    <p className="text-xs text-slate-500 font-medium">Unggah spreadsheet pelanggan (.xlsx)</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Quick Informational Tip Card */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl text-white p-6 shadow-md border border-indigo-700 relative overflow-hidden">
              <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 w-32 h-32 bg-white/5 rounded-full"></div>
              <h4 className="text-sm font-extrabold tracking-wide uppercase text-indigo-200">Tips Operasional</h4>
              <p className="text-xs text-indigo-100 leading-relaxed font-semibold mt-2.5">
                Pastikan data Excel (.xlsx) memiliki kolom IDPEL, UNIT, NAMA, dan LATITUDE/LONGITUDE yang lengkap sebelum diunggah ke database agar plotting tugas berjalan presisi di peta mobile.
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default AdminHomepage;
